import { classifyEntity, type GraphNode, type HuntGraph } from './hunt-graph'
import { ROLE_TONE } from './signal-graph'
import { signalNodeId } from './signal-subgraphs'
import type { Signal } from './signal-model'

/**
 * A case's signals as a graph (docs/cases.md §Case page · Details): every
 * attached signal a node, linked to its own suspects and targets (suspect →
 * signal → target), drawn on the Storylines canvas with the same glyphs
 * (lib/signal-graph.ts). Signals that share an entity join up; the rest
 * stand alone — no weighting, no filter (unlike connected signals). Pure.
 */

/** One attached signal: its row when it could be read, else its snapshot. */
export interface CaseGraphSignal {
  uuid: string
  title: string
  severity: Signal['severity']
  /** The signal's row from the signals service; null when it could not be read (a node without entities). */
  signal: Signal | null
}

/** Signal pings per cycle: one per merged occurrence, capped (as Storylines). */
const MAX_PINGS = 4

const entityKey = (field: string, value: string) => `ent:${field.toLowerCase()}:${value}`

export function caseSignalsGraph(signals: readonly CaseGraphSignal[]): HuntGraph {
  const roles = new Map<string, { field: string; value: string; suspect: boolean; target: boolean; hits: number; fired: number }>()
  const edges: HuntGraph['edges'] = []
  const nodes: GraphNode[] = []
  for (const s of signals) {
    const id = signalNodeId(s.uuid)
    const sev = s.signal?.severity ?? s.severity
    const occurrences = Math.max(1, s.signal?.occurrences ?? 1)
    nodes.push({
      id,
      kind: 'other',
      value: s.signal?.detectionName || s.title,
      field: 'Signal',
      expanded: false,
      tone: ROLE_TONE.signal,
      glyph: { role: 'signal', severity: sev, pings: Math.min(MAX_PINGS, occurrences) },
      ...(s.signal ? { detail: { tactic: s.signal.tactic, technique: s.signal.technique, start: s.signal.at } } : {}),
      ...(occurrences > 1 ? { badge: `×${occurrences}` } : {}),
      title: `${s.signal?.detectionName || s.title} · ${s.signal?.tactic || 'no tactic'} · ${sev}${s.signal ? '' : ' · could not be read'}`,
    })
    for (const e of s.signal?.entities ?? []) {
      const key = entityKey(e.field, e.value)
      const r = roles.get(key) ?? { field: e.field, value: e.value, suspect: false, target: false, hits: 0, fired: 0 }
      if (e.role === 'Suspect') {
        r.suspect = true
        r.fired++
        edges.push({ from: key, to: id, viaQuery: '', directed: true })
      } else {
        r.target = true
        r.hits++
        edges.push({ from: id, to: key, viaQuery: '', directed: true })
      }
      roles.set(key, r)
    }
  }
  for (const [key, r] of roles) {
    const role = r.suspect && r.target ? 'compromised' : r.suspect ? 'suspect' : 'target'
    nodes.push({
      id: key,
      kind: classifyEntity(r.field, r.value) ?? 'other',
      value: r.value,
      field: r.field,
      expanded: false,
      tone: ROLE_TONE[role],
      glyph: { role, kind: r.field, hits: r.hits, fired: r.fired },
      title: `${role} · ${r.field.toLowerCase()} · ${r.value}`,
    })
  }
  return { nodes, edges }
}
